/**
 * Trace Dosing Calculator - Create/Edit batch page
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  Container,
  Title,
  Stack,
  Paper,
  Text,
  Group,
  Badge,
  Table,
  Button,
  TextInput,
  NumberInput,
  Select,
  ActionIcon,
  Tooltip,
  Anchor,
  Modal,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconArrowLeft,
  IconPlus,
  IconX,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  useCreateBatch,
  useUpdateBatch,
  useSaveAsTemplate,
  useDosingTemplates,
} from '@/hooks/useTraceDosing';
import type { DosingBatch, ContainerFormState } from '@/types/traceDosing';

// Available elements (alphabetical)
const ELEMENTS = [
  'Aluminum', 'Barium', 'Chromium', 'Cobalt', 'Copper',
  'Iodine', 'Iron', 'Lead', 'Lithium', 'Manganese',
  'Molybdenum', 'Nickel', 'Selenium', 'Silver', 'Tin',
  'Titanium', 'Tungsten', 'Vanadium', 'Zinc',
];

const BADGE_COLORS = ['blue', 'grape', 'green', 'orange', 'cyan', 'teal'];

interface LocationState {
  template?: DosingBatch;
  copyFrom?: DosingBatch;
  batch?: DosingBatch;
}

// Helper to generate unique local IDs
let localIdCounter = 0;
function generateLocalId(): string {
  return `local-${++localIdCounter}-${Date.now()}`;
}

// Helper to create default container
function createDefaultContainer(): ContainerFormState {
  return {
    localId: generateLocalId(),
    name: 'Dosing Batch 1',
    ml_per_day: 24,
    elements: [],
  };
}

// Helper to convert batch containers to form state
function batchToContainers(batch: DosingBatch): ContainerFormState[] {
  if (batch.containers && batch.containers.length > 0) {
    return batch.containers.map((c) => ({
      localId: generateLocalId(),
      name: c.name,
      ml_per_day: c.ml_per_day,
      elements: c.elements.map(e => ({
        element_name: e.element_name,
        ml_per_day: e.ml_per_day,
      })),
    }));
  }
  // Fallback for batches without containers
  return [{
    localId: generateLocalId(),
    name: batch.name,
    ml_per_day: 24,
    elements: [],
  }];
}

export default function TraceDosingCalculatorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { batchId } = useParams<{ batchId: string }>();
  const state = location.state as LocationState | null;

  const isEditing = !!batchId;
  const createBatchMutation = useCreateBatch();
  const updateBatchMutation = useUpdateBatch();
  const saveAsTemplateMutation = useSaveAsTemplate();
  const { data: existingTemplates = [] } = useDosingTemplates();

  // Form state
  const [batchName, setBatchName] = useState('');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(addDays(new Date(), 30));
  const [days, setDays] = useState(30);
  const [containers, setContainers] = useState<ContainerFormState[]>([createDefaultContainer()]);
  const [notes, setNotes] = useState('');
  const [sourceTemplateName, setSourceTemplateName] = useState<string | null>(null);
  const [savedBatchId, setSavedBatchId] = useState<string | null>(null);

  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [elementSearchValues, setElementSearchValues] = useState<Record<string, string>>({});

  // Template dialog state
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [existingTemplateId, setExistingTemplateId] = useState<string | null>(null);

  // Helper to add days to a date
  function addDays(date: Date, numDays: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + numDays);
    return result;
  }

  // Calculate end date from start date and days
  const calculateEndDate = (start: Date, numDays: number) => {
    setEndDate(addDays(start, numDays));
  };

  // Initialize from state (template, copy, or edit)
  useEffect(() => {
    if (state?.template) {
      // From template
      const formContainers = batchToContainers(state.template);
      setContainers(formContainers);
      setBatchName(state.template.name);
      setDays(state.template.days);
      setNotes(state.template.notes || '');
      setSourceTemplateName(state.template.name);
      calculateEndDate(new Date(), state.template.days);
    } else if (state?.copyFrom) {
      // Copy from existing batch
      const formContainers = batchToContainers(state.copyFrom);
      setContainers(formContainers);
      setBatchName(state.copyFrom.name);
      setDays(state.copyFrom.days);
      setNotes(state.copyFrom.notes || '');
      calculateEndDate(new Date(), state.copyFrom.days);
    } else if (state?.batch && isEditing) {
      // Editing existing batch
      const batch = state.batch;
      setStartDate(new Date(batch.start_date));
      setEndDate(new Date(batch.end_date));
      setDays(batch.days);
      setBatchName(batch.name);
      setNotes(batch.notes || '');
      const formContainers = batchToContainers(batch);
      setContainers(formContainers);
      setSavedBatchId(batch.id);
    } else {
      // Start fresh
      setContainers([createDefaultContainer()]);
      calculateEndDate(new Date(), 30);
    }
  }, [state, isEditing]);

  // Helper to convert YYYY-MM-DD string to Date
  const stringToDate = (str: string | null): Date | null => {
    if (!str) return null;
    return new Date(str + 'T00:00:00');
  };

  // Helper to convert Date to YYYY-MM-DD string
  const dateToString = (date: Date | null): string | null => {
    if (!date) return null;
    return date.toISOString().split('T')[0] ?? null;
  };

  const handleStartDateChange = (value: string | null) => {
    const date = stringToDate(value);
    if (date) {
      setStartDate(date);
      calculateEndDate(date, days);
      markChanged();
    }
  };

  const handleEndDateChange = (value: string | null) => {
    const newEndDate = stringToDate(value);
    if (newEndDate) {
      setEndDate(newEndDate);
      // Calculate days from date difference
      const diffTime = newEndDate.getTime() - startDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 0) {
        setDays(diffDays);
      }
      markChanged();
    }
  };

  const handleDaysChange = (value: number) => {
    setDays(value);
    calculateEndDate(startDate, value);
    markChanged();
  };

  const markChanged = () => {
    setHasChanges(true);
    setSaved(false);
  };

  const updateContainer = (localId: string, updates: Partial<ContainerFormState>) => {
    setContainers(prev => prev.map(c => {
      if (c.localId !== localId) return c;
      return { ...c, ...updates };
    }));
    markChanged();
  };

  const addElement = (containerId: string, elementName: string) => {
    if (elementName === '__custom__') {
      const customName = prompt('Enter custom element name:');
      if (!customName?.trim()) return;
      elementName = customName.trim();
    }

    setContainers(prev => prev.map(c => {
      if (c.localId !== containerId) return c;
      if (c.elements.some(e => e.element_name === elementName)) return c;
      return {
        ...c,
        elements: [...c.elements, { element_name: elementName, ml_per_day: 0.1 }],
      };
    }));
    // Clear the search field after adding element
    setElementSearchValues(prev => ({ ...prev, [containerId]: '' }));
    markChanged();
  };

  const updateElementMlPerDay = (containerId: string, elementName: string, mlPerDay: number) => {
    setContainers(prev => prev.map(c => {
      if (c.localId !== containerId) return c;
      return {
        ...c,
        elements: c.elements.map(e =>
          e.element_name === elementName ? { ...e, ml_per_day: mlPerDay } : e
        ),
      };
    }));
    markChanged();
  };

  const removeElement = (containerId: string, elementName: string) => {
    setContainers(prev => prev.map(c => {
      if (c.localId !== containerId) return c;
      return {
        ...c,
        elements: c.elements.filter(e => e.element_name !== elementName),
      };
    }));
    markChanged();
  };

  const addContainer = () => {
    setContainers(prev => [...prev, {
      localId: generateLocalId(),
      name: `Dosing Batch ${prev.length + 1}`,
      ml_per_day: 24,
      elements: [],
    }]);
    markChanged();
  };

  const removeContainer = (localId: string) => {
    if (containers.length <= 1) return;
    setContainers(prev => prev.filter(c => c.localId !== localId));
    markChanged();
  };

  // Helper to convert form containers to API format
  const containersToApi = () => {
    return containers.map(c => ({
      name: c.name,
      ml_per_day: c.ml_per_day,
      elements: c.elements.map(e => ({
        element_name: e.element_name,
        ml_per_day: e.ml_per_day,
      })),
    }));
  };

  // Helper to format date as YYYY-MM-DD
  const formatDateForApi = (date: Date): string => {
    return date.toISOString().split('T')[0] as string;
  };

  const handleSaveBatch = async () => {
    try {
      // Use first container's name as batch name if not set, or generate one
      const name = batchName || containers[0]?.name || 'Dosing Batch';

      const data = {
        name,
        start_date: formatDateForApi(startDate),
        end_date: formatDateForApi(endDate),
        days,
        notes: notes || null,
        containers: containersToApi(),
      };

      let result: DosingBatch;
      if (isEditing && batchId) {
        result = await updateBatchMutation.mutateAsync({ batchId, batch: data });
      } else if (savedBatchId) {
        // Already saved once in this session, update instead of create
        result = await updateBatchMutation.mutateAsync({ batchId: savedBatchId, batch: data });
      } else {
        result = await createBatchMutation.mutateAsync(data);
        setSavedBatchId(result.id);
      }

      // Show single success notification after save completes
      setSaved(true);
      setHasChanges(false);
      notifications.show({
        title: 'Success',
        message: 'Batch saved successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save batch',
        color: 'red',
      });
    }
  };

  const handleSaveAsTemplateClick = () => {
    // Prefill with sourceTemplateName (if from template) or current batch name
    const prefillName = sourceTemplateName || batchName || containers[0]?.name || '';
    setTemplateName(prefillName);

    // Check if a template with this name already exists
    const existingTemplate = existingTemplates.find(t => t.name === prefillName);
    setExistingTemplateId(existingTemplate?.id || null);

    setShowTemplateDialog(true);
  };

  const handleSaveAsTemplateConfirm = async (overwrite: boolean) => {
    if (!templateName.trim()) {
      notifications.show({
        title: 'Error',
        message: 'Please enter a template name',
        color: 'red',
      });
      return;
    }

    try {
      // First, ensure the batch is saved
      let currentBatchId = savedBatchId || batchId;

      if (!currentBatchId) {
        // Need to save the batch first
        const name = batchName || containers[0]?.name || 'Dosing Batch';
        const data = {
          name,
          start_date: formatDateForApi(startDate),
          end_date: formatDateForApi(endDate),
          days,
          notes: notes || null,
          containers: containersToApi(),
        };
        const result = await createBatchMutation.mutateAsync(data);
        currentBatchId = result.id;
        setSavedBatchId(result.id);
      }

      // Now save as template
      await saveAsTemplateMutation.mutateAsync({
        batchId: currentBatchId,
        request: { name: templateName.trim() },
      });

      setShowTemplateDialog(false);
      setSaved(true);
      setHasChanges(false);

      const message = overwrite && existingTemplateId
        ? `Template "${templateName}" updated successfully`
        : `Template "${templateName}" saved successfully`;

      notifications.show({
        title: 'Success',
        message,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save template',
        color: 'red',
      });
    }
  };

  const handleTemplateNameChange = (name: string) => {
    setTemplateName(name);
    // Check if a template with this name already exists
    const existingTemplate = existingTemplates.find(t => t.name === name);
    setExistingTemplateId(existingTemplate?.id || null);
  };

  const handleBack = () => {
    if (hasChanges && !confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return;
    }
    navigate('/trace-dosing');
  };

  const handleDiscard = () => {
    if (hasChanges && !confirm('Are you sure you want to discard this batch?')) {
      return;
    }
    navigate('/trace-dosing');
  };

  const pageTitle = isEditing
    ? 'Edit Batch'
    : (state?.template
      ? 'New Batch from Template'
      : (state?.copyFrom ? 'New Batch (Copy)' : 'Create New Batch'));

  const isSaving = createBatchMutation.isPending || updateBatchMutation.isPending || saveAsTemplateMutation.isPending;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Stack gap="xs">
          <Anchor component="button" onClick={handleBack} c="dimmed" size="sm">
            <Group gap={6}>
              <IconArrowLeft size={16} />
              Back to Batches
            </Group>
          </Anchor>
          <Group justify="space-between">
            <Title order={1}>{pageTitle}</Title>
            {saved && (
              <Group gap={6} c="green">
                <IconCheck size={16} />
                <Text size="sm" fw={500}>Saved</Text>
              </Group>
            )}
          </Group>
        </Stack>

        {/* Batch Settings */}
        <Paper p="md" withBorder>
          <Stack gap="md">
            <TextInput
              label="Batch Name"
              value={batchName}
              onChange={(e) => { setBatchName(e.target.value); markChanged(); }}
              placeholder="e.g., Anions March 2026"
              style={{ maxWidth: 300 }}
            />
            <Group gap="xl" wrap="wrap">
              <DatePickerInput
                label="Start Date"
                value={dateToString(startDate)}
                onChange={handleStartDateChange}
                style={{ width: 180 }}
                valueFormat="MMM DD, YYYY"
              />
              <NumberInput
                label="Days"
                value={days}
                onChange={(val) => handleDaysChange(Number(val) || 30)}
                min={1}
                max={365}
                style={{ width: 80 }}
              />
              <DatePickerInput
                label="Planned End Date"
                value={dateToString(endDate)}
                onChange={handleEndDateChange}
                style={{ width: 180 }}
                valueFormat="MMM DD, YYYY"
                minDate={startDate}
              />
            </Group>
          </Stack>
        </Paper>

        {/* Batch Containers */}
        {containers.map((container) => {
          const totalVolume = days * container.ml_per_day;
          const elementsVolume = container.elements.reduce(
            (sum, el) => sum + el.ml_per_day * days, 0
          );
          const rodiVolume = totalVolume - elementsVolume;

          const availableElements = ELEMENTS.filter(
            e => !container.elements.some(el => el.element_name === e)
          );

          return (
            <Paper key={container.localId} withBorder>
              {/* Header */}
              <Group
                gap="md"
                wrap="wrap"
                p="md"
                style={{ backgroundColor: 'var(--mantine-color-gray-0)', borderBottom: '1px solid var(--mantine-color-gray-3)' }}
              >
                <TextInput
                  value={container.name}
                  onChange={(e) => updateContainer(container.localId, { name: e.target.value })}
                  variant="filled"
                  fw={600}
                  size="md"
                  style={{ width: 200 }}
                  placeholder="Batch name"
                />
                <Group gap="xs">
                  <Text size="sm" c="dimmed">ml/day:</Text>
                  <NumberInput
                    value={container.ml_per_day}
                    onChange={(val) => updateContainer(container.localId, { ml_per_day: Number(val) || 24 })}
                    min={0.1}
                    step={0.1}
                    decimalScale={1}
                    size="xs"
                    style={{ width: 70 }}
                  />
                </Group>
                {containers.length > 1 && (
                  <ActionIcon variant="subtle" color="gray" onClick={() => removeContainer(container.localId)}>
                    <IconX size={16} />
                  </ActionIcon>
                )}
              </Group>

              {/* Elements Table */}
              <div style={{ padding: 16 }}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '40%' }}>Element</Table.Th>
                      <Table.Th style={{ width: '25%' }}>ml/day</Table.Th>
                      <Table.Th style={{ width: '10%' }}></Table.Th>
                      <Table.Th style={{ width: '25%', textAlign: 'right' }}>Total ml per Batch</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {container.elements.length === 0 ? (
                      <Table.Tr>
                        <Table.Td colSpan={4} style={{ textAlign: 'center', padding: 24 }}>
                          <Text c="dimmed">No elements added yet. Use the dropdown below to add elements.</Text>
                        </Table.Td>
                      </Table.Tr>
                    ) : (
                      container.elements.map((el, elIndex) => (
                        <Table.Tr key={el.element_name}>
                          <Table.Td>
                            <Badge variant="light" color={BADGE_COLORS[elIndex % BADGE_COLORS.length]}>
                              {el.element_name}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <NumberInput
                              value={el.ml_per_day}
                              onChange={(val) => updateElementMlPerDay(container.localId, el.element_name, Number(val) || 0)}
                              min={0}
                              step={0.01}
                              decimalScale={2}
                              allowDecimal
                              allowNegative={false}
                              size="xs"
                              style={{ width: 70 }}
                            />
                          </Table.Td>
                          <Table.Td>
                            <ActionIcon variant="subtle" color="gray" onClick={() => removeElement(container.localId, el.element_name)}>
                              <IconX size={14} />
                            </ActionIcon>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            {(el.ml_per_day * days).toFixed(1)} ml
                          </Table.Td>
                        </Table.Tr>
                      ))
                    )}
                  </Table.Tbody>
                  {container.elements.length > 0 && (
                    <Table.Tfoot>
                      <Table.Tr style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                        <Table.Td colSpan={3}><Text fw={600} c="blue">RO/DI Water</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          <Text c="blue">{rodiVolume.toFixed(1)} ml</Text>
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr style={{ backgroundColor: 'var(--mantine-color-blue-1)' }}>
                        <Table.Td colSpan={3}><Text fw={700}>Total Volume</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          <Text fw={700}>{totalVolume.toFixed(0)} ml</Text>
                        </Table.Td>
                      </Table.Tr>
                    </Table.Tfoot>
                  )}
                </Table>

                {/* Add Element */}
                <Select
                  placeholder="+ Add Element"
                  data={[
                    ...availableElements.map(e => ({ value: e, label: e })),
                    { value: '__custom__', label: '+ Custom Element...' },
                  ]}
                  value={null}
                  onChange={(val) => val && addElement(container.localId, val)}
                  searchable
                  searchValue={elementSearchValues[container.localId] || ''}
                  onSearchChange={(val) => setElementSearchValues(prev => ({ ...prev, [container.localId]: val }))}
                  clearable={false}
                  style={{ width: 220, marginTop: 16 }}
                />
              </div>
            </Paper>
          );
        })}

        {/* Add Container Button */}
        <Button variant="outline" leftSection={<IconPlus size={16} />} onClick={addContainer}>
          Add Another Batch Container
        </Button>

        {/* Notes */}
        <Textarea
          label="Notes"
          placeholder="e.g., Don't increase daily Iodine beyond 0.5 ml, batch lasts longer in cool storage..."
          value={notes}
          onChange={(e) => { setNotes(e.target.value); markChanged(); }}
          minRows={2}
          autosize
          maxRows={6}
        />

        {/* Footer Actions */}
        <Paper p="md" withBorder>
          <Group justify="space-between" wrap="wrap">
            <Button variant="subtle" color="gray" onClick={handleDiscard}>
              Discard
            </Button>
            <Group>
              <Tooltip
                label="Save your element configuration as a reusable template. Next time you make a batch, you can start from this template instead of entering everything again."
                multiline
                w={280}
              >
                <Button
                  variant="outline"
                  rightSection={<IconInfoCircle size={14} />}
                  onClick={handleSaveAsTemplateClick}
                  loading={isSaving}
                >
                  Save as Template
                </Button>
              </Tooltip>
              <Button
                onClick={handleSaveBatch}
                loading={isSaving}
              >
                Save Batch
              </Button>
            </Group>
          </Group>
        </Paper>
      </Stack>

      {/* Save as Template Dialog */}
      <Modal
        opened={showTemplateDialog}
        onClose={() => setShowTemplateDialog(false)}
        title="Save as Template"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Template Name"
            placeholder="Enter template name"
            value={templateName}
            onChange={(e) => handleTemplateNameChange(e.target.value)}
            autoFocus
          />

          {existingTemplateId && (
            <Text size="sm" c="orange">
              A template with this name already exists. Saving will overwrite it.
            </Text>
          )}

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setShowTemplateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSaveAsTemplateConfirm(!!existingTemplateId)}
              loading={saveAsTemplateMutation.isPending}
            >
              {existingTemplateId ? 'Overwrite Template' : 'Save Template'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
